"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.downloadStudentStatusPdf = exports.openStudentStatusPdf = exports.toPeriodFileToken = void 0;
const jspdf_1 = __importDefault(require("jspdf"));
const FONT_FILE_NAME = 'NotoSansKR-Regular.ttf';
const FONT_FAMILY = 'NotoSansKR';
const PAGE_MARGIN_X = 10;
const PAGE_TOP = 10;
const PAGE_BOTTOM = 12;
const FOOTER_SPACE = 8;
const HEADER_TITLE_HEIGHT = 10;
const HEADER_TOP_ROW_HEIGHT = 10;
const HEADER_META_LINE_HEIGHT = 6;
const HEADER_META_GAP = 2;
const HEADER_BLOCK_GAP = 8;
const SUMMARY_GROUP_TITLE_HEIGHT = 10;
const SUMMARY_CARD_HEIGHT = 24;
const SUMMARY_GROUP_GAP = 6;
const SUMMARY_CARD_GAP = 4;
const SUMMARY_BOTTOM_GAP = 8;
const SUMMARY_BAR_HEIGHT = 5;
const SUMMARY_BAR_COLOR = [139, 195, 208];
const HISTORY_TITLE_HEIGHT = 10;
const SECTION_TITLE_HEIGHT = 8;
const SECTION_TITLE_TABLE_GAP = 2;
const TABLE_HEADER_HEIGHT = 8;
const TABLE_ROW_HEIGHT = 8;
const TABLE_EMPTY_HEIGHT = 15;
const BLOCK_GAP = 6;
const detailSectionTitles = {
    attendance: '출결',
    test: '테스트',
    assignment: '과제',
};
const detailSectionFilterLabels = {
    attendance: '표시 출결 상태:',
    test: '표시 응시 상태:',
    assignment: '표시 응시 상태:',
};
const statusLabelMap = {
    attendance: {
        attend: '출석',
        late: '지각',
        absent: '결석',
    },
    test: {
        submit: '응시',
        'not-submit': '미응시',
    },
    assignment: {
        submit: '응시',
        'not-submit': '미응시',
    },
};
let fontLoadPromise = null;
let activeFontFamily = FONT_FAMILY;
const toBase64 = (bytes) => {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
};
const loadKoreanFontBase64 = async () => {
    if (!fontLoadPromise) {
        fontLoadPromise = fetch(`/libs/${FONT_FILE_NAME}`)
            .then(res => {
            if (!res.ok) {
                throw new Error(`Failed to load font: ${res.status}`);
            }
            return res.arrayBuffer();
        })
            .then(buffer => toBase64(new Uint8Array(buffer)));
    }
    return fontLoadPromise;
};
const trimText = (pdf, text, maxWidth) => {
    if (pdf.getTextWidth(text) <= maxWidth)
        return text;
    let result = text;
    while (result.length > 1 && pdf.getTextWidth(`${result}...`) > maxWidth) {
        result = result.slice(0, -1);
    }
    return `${result}...`;
};
const clampPercentage = (value) => {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.min(100, value));
};
const formatFilterStatuses = (segment, statuses) => {
    if (!statuses.length)
        return '없음';
    return statuses.map(status => statusLabelMap[segment][status] || status).join(', ');
};
// 기간 라벨('YYYY.MM.DD ~ YYYY.MM.DD')을 파일명 안전 토큰('YYYYMMDD-YYYYMMDD')으로 변환
const toPeriodFileToken = (label) => label.replace(/\./g, '').replace(/\s*~\s*/g, '-');
exports.toPeriodFileToken = toPeriodFileToken;
const buildHeaderLines = (_payload) => [];
const getHeaderHeight = (headerLines) => HEADER_TOP_ROW_HEIGHT + headerLines.length * HEADER_META_LINE_HEIGHT + HEADER_META_GAP + HEADER_BLOCK_GAP;
const getSummaryHeight = (groups) => groups.reduce((height, group, index) => {
    const columnCount = group.columns || 3;
    const rows = Math.max(1, Math.ceil(group.cards.length / columnCount));
    const groupHeight = SUMMARY_GROUP_TITLE_HEIGHT +
        rows * SUMMARY_CARD_HEIGHT +
        Math.max(0, rows - 1) * SUMMARY_CARD_GAP;
    return height + groupHeight + (index < groups.length - 1 ? SUMMARY_GROUP_GAP : 0);
}, 0) + SUMMARY_BOTTOM_GAP;
const getBlockHeight = (rowsCount) => SECTION_TITLE_HEIGHT +
    SECTION_TITLE_TABLE_GAP +
    TABLE_HEADER_HEIGHT +
    (rowsCount > 0 ? rowsCount * TABLE_ROW_HEIGHT : TABLE_EMPTY_HEIGHT);
const getRowsFit = (remainingHeight) => {
    const usable = remainingHeight - SECTION_TITLE_HEIGHT - SECTION_TITLE_TABLE_GAP - TABLE_HEADER_HEIGHT;
    if (usable < TABLE_ROW_HEIGHT)
        return 0;
    return Math.floor(usable / TABLE_ROW_HEIGHT);
};
const buildPagePlan = (payload, pageHeight) => {
    const maxContentHeight = pageHeight - PAGE_TOP - PAGE_BOTTOM - FOOTER_SPACE;
    const pages = [];
    const headerLines = buildHeaderLines(payload);
    const headerHeight = getHeaderHeight(headerLines);
    const summaryHeight = getSummaryHeight(payload.summaryGroups);
    const sectionState = payload.sections.map(section => ({
        ...section,
        index: 0,
        continueCount: 1,
    }));
    let sectionCursor = 0;
    let isFirstPage = true;
    let needsHistoryTitle = true;
    while (sectionCursor < sectionState.length) {
        let remaining = maxContentHeight;
        const page = {
            showHeader: isFirstPage,
            showSummary: isFirstPage,
            showHistoryTitle: false,
            blocks: [],
        };
        if (isFirstPage) {
            remaining -= headerHeight + summaryHeight;
        }
        if (needsHistoryTitle) {
            if (remaining < HISTORY_TITLE_HEIGHT + TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT + SECTION_TITLE_HEIGHT) {
                pages.push(page);
                isFirstPage = false;
                continue;
            }
            page.showHistoryTitle = true;
            remaining -= HISTORY_TITLE_HEIGHT;
            needsHistoryTitle = false;
        }
        while (sectionCursor < sectionState.length) {
            const section = sectionState[sectionCursor];
            const remainingRows = section.rows.length - section.index;
            if (remainingRows <= 0) {
                page.blocks.push({
                    title: section.title,
                    studentType: section.studentType,
                    rows: [],
                    isEmpty: true,
                });
                remaining -= getBlockHeight(0);
                sectionCursor += 1;
                if (sectionCursor < sectionState.length) {
                    remaining -= BLOCK_GAP;
                    if (remaining <= 0)
                        break;
                }
                continue;
            }
            const availableRows = getRowsFit(remaining);
            if (availableRows <= 0)
                break;
            const take = Math.min(remainingRows, availableRows);
            page.blocks.push({
                title: section.index === 0 ? section.title : `${section.title} (계속 ${section.continueCount})`,
                studentType: section.studentType,
                rows: section.rows.slice(section.index, section.index + take),
            });
            section.index += take;
            remaining -= getBlockHeight(take);
            if (section.index < section.rows.length) {
                section.continueCount += 1;
                break;
            }
            sectionCursor += 1;
            if (sectionCursor < sectionState.length) {
                remaining -= BLOCK_GAP;
                if (remaining <= 0)
                    break;
            }
        }
        if (page.blocks.length === 0 && sectionCursor < sectionState.length) {
            const section = sectionState[sectionCursor];
            page.blocks.push({
                title: section.index === 0 ? section.title : `${section.title} (계속 ${section.continueCount})`,
                studentType: section.studentType,
                rows: section.rows.slice(section.index, section.index + 1),
            });
            section.index += 1;
            if (section.index < section.rows.length) {
                section.continueCount += 1;
            }
            else {
                sectionCursor += 1;
            }
        }
        pages.push(page);
        isFirstPage = false;
    }
    if (pages.length === 0) {
        pages.push({
            showHeader: true,
            showSummary: true,
            showHistoryTitle: true,
            blocks: [],
        });
    }
    return pages;
};
const setPdfFont = (pdf, style = 'normal') => {
    pdf.setFont(activeFontFamily, style);
};
const drawHeader = (pdf, payload, startY) => {
    const headerLines = buildHeaderLines(payload);
    const pageWidth = pdf.internal.pageSize.getWidth();
    setPdfFont(pdf, 'bold');
    pdf.setFontSize(20);
    pdf.text('학생별 현황', PAGE_MARGIN_X, startY + 7);
    setPdfFont(pdf, 'normal');
    pdf.setFontSize(10);
    const keywordText = `검색어 ${payload.filters.keyword || '-'}`;
    const refundedText = `환불 포함 ${payload.filters.includeRefunded ? 'O' : 'X'}`;
    // weekLabel 은 기간 라벨('YYYY.MM.DD ~ YYYY.MM.DD') — 주차 → 기간 직접 지정 전환(필드명은 diff 확산 방지로 유지)
    const weekText = `기간 ${payload.weekLabel}`;
    const generatedAtText = `기준일시 ${payload.generatedAt}`;
    const keywordWidth = pdf.getTextWidth(keywordText);
    const refundedWidth = pdf.getTextWidth(refundedText);
    const weekWidth = pdf.getTextWidth(weekText);
    const generatedAtWidth = pdf.getTextWidth(generatedAtText);
    const metaGap = 8;
    const weekX = pageWidth - PAGE_MARGIN_X - weekWidth;
    const generatedAtX = weekX - metaGap - generatedAtWidth;
    const refundedX = generatedAtX - metaGap - refundedWidth;
    const keywordX = refundedX - metaGap - keywordWidth;
    pdf.text(keywordText, keywordX, startY + 5.5);
    pdf.text(refundedText, refundedX, startY + 5.5);
    pdf.text(generatedAtText, generatedAtX, startY + 5.5);
    pdf.text(weekText, weekX, startY + 5.5);
    return startY + getHeaderHeight(headerLines);
};
const drawSummaryGroups = (pdf, groups, startY) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - PAGE_MARGIN_X * 2;
    let cursorY = startY;
    groups.forEach((group, groupIndex) => {
        const columnCount = group.columns || 3;
        const boxWidth = (contentWidth - SUMMARY_CARD_GAP * (columnCount - 1)) / columnCount;
        setPdfFont(pdf, 'bold');
        pdf.setFontSize(16);
        pdf.text(group.title, PAGE_MARGIN_X, cursorY + 6);
        cursorY += SUMMARY_GROUP_TITLE_HEIGHT;
        group.cards.forEach((card, index) => {
            const rowIndex = Math.floor(index / columnCount);
            const columnIndex = index % columnCount;
            const x = PAGE_MARGIN_X + columnIndex * (boxWidth + SUMMARY_CARD_GAP);
            const y = cursorY + rowIndex * (SUMMARY_CARD_HEIGHT + SUMMARY_CARD_GAP);
            pdf.setDrawColor(220, 224, 230);
            pdf.setFillColor(249, 250, 252);
            pdf.roundedRect(x, y, boxWidth, SUMMARY_CARD_HEIGHT, 2, 2, 'FD');
            setPdfFont(pdf, 'normal');
            pdf.setFontSize(11);
            pdf.text(trimText(pdf, card.title, boxWidth - 8), x + 4, y + 7);
            setPdfFont(pdf, 'bold');
            pdf.setFontSize(18);
            if (card.progress !== undefined) {
                const progress = clampPercentage(card.progress);
                const contentX = x + 4;
                const percentText = card.valueText;
                const percentWidth = Math.max(20, pdf.getTextWidth(percentText));
                const barGap = 6;
                const barX = contentX + percentWidth + barGap;
                const barWidth = Math.max(16, boxWidth - 8 - percentWidth - barGap);
                const rowCenterY = y + 15;
                const barY = rowCenterY - SUMMARY_BAR_HEIGHT / 2;
                pdf.text(percentText, contentX, rowCenterY + 2.8);
                pdf.setFillColor(229, 230, 234);
                pdf.rect(barX, barY, barWidth, SUMMARY_BAR_HEIGHT, 'F');
                if (progress > 0) {
                    pdf.setFillColor(SUMMARY_BAR_COLOR[0], SUMMARY_BAR_COLOR[1], SUMMARY_BAR_COLOR[2]);
                    pdf.rect(barX, barY, (barWidth * progress) / 100, SUMMARY_BAR_HEIGHT, 'F');
                }
            }
            else {
                pdf.text(card.valueText, x + 4, y + 16);
            }
            if (card.subtitle && card.progress === undefined) {
                setPdfFont(pdf, 'normal');
                pdf.setFontSize(9);
                pdf.text(trimText(pdf, card.subtitle, boxWidth - 8), x + 4, y + 21);
            }
        });
        cursorY +=
            Math.max(1, Math.ceil(group.cards.length / columnCount)) * SUMMARY_CARD_HEIGHT +
                Math.max(0, Math.ceil(group.cards.length / columnCount) - 1) * SUMMARY_CARD_GAP;
        if (groupIndex < groups.length - 1) {
            cursorY += SUMMARY_GROUP_GAP;
        }
    });
    return cursorY + SUMMARY_BOTTOM_GAP;
};
const getColumnSpecs = (studentType) => {
    const ratios = [1, 2.8, 1, 1, 1, 1, 1];
    const headers = studentType === 'attend'
        ? ['날짜', '강의명', '성명', 'ID', '출결상태', '참여시간', '재원상태']
        : ['날짜', studentType === 'test' ? '테스트명' : '과제명', '성명', 'ID', '응시상태', '성적', '재원상태'];
    return { ratios, headers };
};
const drawSectionBlock = (pdf, block, startY, contentWidth, filterText) => {
    const { ratios, headers } = getColumnSpecs(block.studentType);
    const ratioSum = ratios.reduce((acc, ratio) => acc + ratio, 0);
    const colWidths = ratios.map(ratio => (contentWidth * ratio) / ratioSum);
    const pageWidth = pdf.internal.pageSize.getWidth();
    setPdfFont(pdf, 'bold');
    pdf.setFontSize(14);
    pdf.text(block.title, PAGE_MARGIN_X, startY + 6);
    if (filterText) {
        setPdfFont(pdf, 'normal');
        pdf.setFontSize(10);
        pdf.text(filterText, pageWidth - PAGE_MARGIN_X, startY + 5.8, { align: 'right' });
    }
    const tableTopY = startY + SECTION_TITLE_HEIGHT + SECTION_TITLE_TABLE_GAP;
    pdf.setDrawColor(196, 200, 206);
    pdf.setFillColor(243, 244, 248);
    pdf.rect(PAGE_MARGIN_X, tableTopY, contentWidth, TABLE_HEADER_HEIGHT, 'FD');
    let cursorX = PAGE_MARGIN_X;
    setPdfFont(pdf, 'normal');
    pdf.setFontSize(10);
    headers.forEach((header, idx) => {
        const width = colWidths[idx];
        pdf.rect(cursorX, tableTopY, width, TABLE_HEADER_HEIGHT);
        pdf.text(header, cursorX + width / 2, tableTopY + 5.5, { align: 'center' });
        cursorX += width;
    });
    if (block.isEmpty || block.rows.length === 0) {
        const emptyY = tableTopY + TABLE_HEADER_HEIGHT;
        pdf.rect(PAGE_MARGIN_X, emptyY, contentWidth, TABLE_EMPTY_HEIGHT);
        setPdfFont(pdf, 'normal');
        pdf.setFontSize(12);
        pdf.setTextColor(123, 125, 132);
        pdf.text('조건에 맞는 데이터가 없습니다.', PAGE_MARGIN_X + contentWidth / 2, emptyY + TABLE_EMPTY_HEIGHT / 2 + 1, {
            align: 'center',
        });
        pdf.setTextColor(16, 16, 16);
        return emptyY + TABLE_EMPTY_HEIGHT;
    }
    const safeRows = block.rows;
    safeRows.forEach((row, rowIdx) => {
        const y = tableTopY + TABLE_HEADER_HEIGHT + rowIdx * TABLE_ROW_HEIGHT;
        let cellX = PAGE_MARGIN_X;
        const values = block.studentType === 'attend'
            ? [
                row?.date || '-',
                row?.className || '-',
                row?.studentName || '-',
                row?.studentId || '-',
                row?.status === 'attend'
                    ? '출석'
                    : row?.status === 'late'
                        ? '지각'
                        : '결석',
                row?.durationMinutes !== null &&
                    row?.durationMinutes !== undefined
                    ? `${row.durationMinutes}분`
                    : '-',
                row?.enrolledStatus || '-',
            ]
            : [
                row?.date || '-',
                row?.title || '-',
                row?.studentName || '-',
                row?.studentId || '-',
                row?.status === 'submit' ? '응시' : '미응시',
                row?.score !== null && row?.score !== undefined
                    ? String(row.score)
                    : '-',
                row?.enrolledStatus || '-',
            ];
        values.forEach((value, idx) => {
            const width = colWidths[idx];
            pdf.rect(cellX, y, width, TABLE_ROW_HEIGHT);
            const isCenter = idx === 0 || idx >= 2;
            const text = trimText(pdf, String(value ?? '-'), width - 3);
            if (isCenter) {
                pdf.text(text, cellX + width / 2, y + 5.3, { align: 'center' });
            }
            else {
                pdf.text(text, cellX + 1.6, y + 5.3);
            }
            cellX += width;
        });
    });
    return tableTopY + TABLE_HEADER_HEIGHT + safeRows.length * TABLE_ROW_HEIGHT;
};
const addPageNumbers = (pdf) => {
    const totalPages = pdf.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    for (let i = 1; i <= totalPages; i += 1) {
        pdf.setPage(i);
        setPdfFont(pdf, 'normal');
        pdf.setFontSize(9);
        pdf.text(`${i}/${totalPages}`, pageWidth / 2, pageHeight - 4, { align: 'center' });
    }
};
const buildPdfPayload = (generatedAt, weekLabel, weeklyData, filters) => ({
    generatedAt,
    weekLabel,
    filters,
    summaryGroups: [
        {
            title: '인원 현황',
            columns: 2,
            cards: weeklyData.peopleSummary.map(item => ({
                key: item.id,
                title: item.label,
                valueText: `${item.value}${item.unit || ''}`,
            })),
        },
        {
            title: '학습 현황',
            columns: 3,
            cards: weeklyData.learningSummary.map(item => ({
                key: item.id,
                title: item.label,
                valueText: `${item.value}%`,
                progress: item.value,
            })),
        },
    ],
    sections: [
        {
            id: 'attendance',
            title: detailSectionTitles.attendance,
            studentType: 'attend',
            rows: weeklyData.attendanceRows,
        },
        {
            id: 'test',
            title: detailSectionTitles.test,
            studentType: 'test',
            rows: weeklyData.testRows,
        },
        {
            id: 'assignment',
            title: detailSectionTitles.assignment,
            studentType: 'task',
            rows: weeklyData.assignmentRows,
        },
    ],
});
const buildStudentStatusPdfDoc = async ({ generatedAt, selectedWeek, filters, }) => {
    const payload = buildPdfPayload(generatedAt, selectedWeek.label, selectedWeek, filters);
    const pdf = new jspdf_1.default('p', 'mm', 'a4');
    pdf.setProperties({ title: `student-status-${(0, exports.toPeriodFileToken)(payload.weekLabel)}` });
    try {
        const fontBase64 = await loadKoreanFontBase64();
        pdf.addFileToVFS(FONT_FILE_NAME, fontBase64);
        pdf.addFont(FONT_FILE_NAME, FONT_FAMILY, 'normal');
        pdf.addFont(FONT_FILE_NAME, FONT_FAMILY, 'bold');
        activeFontFamily = FONT_FAMILY;
        setPdfFont(pdf, 'normal');
    }
    catch (error) {
        console.warn('Failed to load Korean font for PDF. Fallback font will be used.', error);
        activeFontFamily = 'helvetica';
        setPdfFont(pdf, 'normal');
    }
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - PAGE_MARGIN_X * 2;
    const pages = buildPagePlan(payload, pageHeight);
    pages.forEach((plan, pageIndex) => {
        if (pageIndex > 0) {
            pdf.addPage();
        }
        let cursorY = PAGE_TOP;
        if (plan.showHeader) {
            cursorY = drawHeader(pdf, payload, cursorY);
        }
        if (plan.showSummary) {
            cursorY = drawSummaryGroups(pdf, payload.summaryGroups, cursorY);
        }
        if (plan.showHistoryTitle) {
            setPdfFont(pdf, 'bold');
            pdf.setFontSize(18);
            pdf.text('상세 내역', PAGE_MARGIN_X, cursorY + 7);
            cursorY += HISTORY_TITLE_HEIGHT;
        }
        plan.blocks.forEach((block, index) => {
            const sectionId = block.studentType === 'attend'
                ? 'attendance'
                : block.studentType === 'test'
                    ? 'test'
                    : 'assignment';
            const filterText = `${detailSectionFilterLabels[sectionId]} ${formatFilterStatuses(sectionId, payload.filters.statuses[sectionId])}`;
            cursorY = drawSectionBlock(pdf, block, cursorY, contentWidth, filterText);
            if (index < plan.blocks.length - 1) {
                cursorY += BLOCK_GAP;
            }
        });
    });
    addPageNumbers(pdf);
    return { pdf, weekLabel: payload.weekLabel };
};
const openStudentStatusPdf = async (args) => {
    APP.eventManager.publish('SHOW_LOADING', true);
    try {
        const { pdf, weekLabel } = await buildStudentStatusPdfDoc(args);
        return new Uint8Array(pdf.output('arraybuffer'));
    }
    finally {
        APP.eventManager.publish('SHOW_LOADING', false);
    }
};
exports.openStudentStatusPdf = openStudentStatusPdf;
const downloadStudentStatusPdf = async (args) => {
    APP.eventManager.publish('SHOW_LOADING', true);
    try {
        const { pdf, weekLabel } = await buildStudentStatusPdfDoc(args);
        const filename = `student-status-${(0, exports.toPeriodFileToken)(weekLabel)}.pdf`;
        const blob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }
    finally {
        APP.eventManager.publish('SHOW_LOADING', false);
    }
};
exports.downloadStudentStatusPdf = downloadStudentStatusPdf;
